<template>
  <v-card>
    <v-data-table
      :items="filteredTagStats"
      :headers="headers"
      :loading="$apollo.loading"
      class="safety-elevation-3 table-striped mt-3"
      :items-per-page="$vuetify.breakpoint.lgAndDown ? 5 : 10"
      :sort-by="['tag.category', 'employeeIds']"
      :sort-desc="[true, true]"
      footer.page-text="page text"
      :search="search"
      :custom-filter="searchFilter"
      :footer-props="{
        'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
        'items-per-page-all-text': `${$t('employees.footer.all')}`,
      }"
    >
      <template v-slot:top>
        <v-row justify="end" class="ma-3">
          <v-text-field
            v-model="search"
            :label="$t('search.title')"
            class="flex-grow-1 pt-3 mr-3"
            prepend-inner-icon="search"
            single-line
            :background-color="
              $vuetify.theme.themes.light.primary | hexToRgb(0.2)
            "
            solo
            dense
            flat
            hide-details
            clearable
          />
          <add-new-tag
            tagCategory="position"
            @refresh="$apollo.queries.tagStats.refetch()"
          />
          <add-new-tag
            tagCategory="collar"
            @refresh="$apollo.queries.tagStats.refetch()"
          />

          <v-btn
            :text="!showActive"
            color="primary"
            class="mt-3"
            @click="showActive = true"
            >{{ $t("instructions.tabs.active") }}
          </v-btn>
          <v-btn
            :text="showActive"
            color="primary"
            class="mt-3"
            @click="showActive = false"
            >{{ $t("instructions.tabs.inactive") }}
          </v-btn>
        </v-row>
      </template>

      <template v-slot:[`item.tag.description`]="{ item }">
        <span v-if="!newDescription || newDescription.id != item.tag.id">{{
          item.tag.description
        }}</span>
        <v-icon
          small
          right
          v-if="!newDescription && role('admin', 'safetyRepresentative')"
          @click="newDescription = { id: item.tag.id }"
        >
          edit
        </v-icon>

        <v-text-field
          v-if="newDescription && newDescription.id == item.tag.id"
          v-model="newDescription.value"
          style="min-width: 250px"
        >
          <template v-slot:append>
            <v-icon icon small @click="newDescription = null">close</v-icon>
          </template>
          <template v-slot:append-outer>
            <v-icon
              icon
              right
              @click="changeTagDescription(item.tag, newDescription.value)"
            >
              save
            </v-icon>
          </template>
        </v-text-field>
      </template>

      <template v-slot:[`item.tag.name`]="{ item }">
        <span v-if="!newName || newName.id != item.tag.id">{{
          item.tag.name
        }}</span>
        <v-icon
          small
          right
          v-if="
            !newName &&
              !['department', 'location', 'shift'].includes(
                item.tag.category
              ) &&
              role('admin', 'safetyRepresentative')
          "
          @click="newName = { id: item.tag.id }"
        >
          edit
        </v-icon>

        <v-text-field
          v-if="newName && newName.id == item.tag.id"
          v-model="newName.value"
          style="min-width: 250px"
        >
          <template v-slot:append>
            <v-icon icon small @click="newName = null">close</v-icon>
          </template>
          <template v-slot:append-outer>
            <v-icon icon right @click="changeTagName(item.tag, newName.value)">
              save
            </v-icon>
          </template>
        </v-text-field>
      </template>

      <template v-slot:[`item.tag.explanation`]="{ item }">
        <span v-if="!newExplanation || newExplanation.id != item.tag.id">
          {{ item.tag.explanation }}
        </span>
        <v-icon
          small
          right
          v-if="!newExplanation && role('admin', 'safetyRepresentative')"
          @click="newExplanation = { id: item.tag.id }"
        >
          edit
        </v-icon>

        <v-textarea
          v-if="newExplanation && newExplanation.id == item.tag.id"
          v-model="newExplanation.value"
          rows="1"
          auto-grow
          style="min-width: 250px"
          counter="256"
        >
          <template v-slot:append>
            <v-icon icon small @click="newExplanation = null">close</v-icon>
          </template>
          <template v-slot:append-outer>
            <v-icon
              icon
              right
              @click="changeTagExplanation(item.tag, newExplanation.value)"
            >
              save
            </v-icon>
          </template>
        </v-textarea>
      </template>

      <template v-slot:[`item.relatedTags`]="{ item }">
        <span>
          <template v-for="(rt, i) in item.relatedTags">
            <v-chip
              small
              :key="i"
              @click="search += ` ${rt.name}`"
              :style="rt.isActive ? '' : 'text-decoration: line-through;'"
            >
              {{ rt.name }}
            </v-chip>
          </template>
        </span>
      </template>

      <template v-slot:[`item.tag.category`]="{ item }">
        <v-chip small @click="search += ` ${item.tag.category}`">
          {{ item.tag.category }}
        </v-chip>
      </template>

      <template v-slot:[`item.tag.id`]="{ item }">
        <span
          :style="item.tag.isActive ? '' : 'text-decoration: line-through;'"
          >{{ item.tag.id }}</span
        >
      </template>
      <template v-slot:[`item.employeeIds`]="{ item }">
        <span @click="$set(item, 'tagIds', [item.tag.id])">
          <employees-table-dialog-with-query
            :variables="item.tagIds ? { tagIds: item.tagIds } : null"
            :text="
              `${
                item.tagIds ? '' : item.employeeIds && item.employeeIds.length
              }`
            "
            :title="item.tag.description ? item.tag.description : item.tag.name"
          />
        </span>
      </template>

      <template v-slot:[`item.instructionIds`]="{ item }">
        <span @click="$set(item, 'tagIds', [item.tag.id])">
          <instructions-table-dialog-with-query
            :variables="item.tagIds ? { tagIds: item.tagIds } : null"
            :text="
              `${
                item.tagIds
                  ? ''
                  : item.instructionIds && item.instructionIds.length
              }`
            "
            :title="item.tag.description ? item.tag.description : item.tag.name"
          />
        </span>
      </template>

      <template v-slot:[`item.managers`]="{ item }">
        <span>
          <template v-for="(m, i) in item.managers">
            <v-chip small :key="i">
              {{ m | name }}
            </v-chip>
          </template>
        </span>
      </template>
      <template v-slot:[`item.menu`]="{ item }">
        <div style="display: inline-flex">
          <v-btn
            icon
            v-if="item.relatedTags && item.relatedTags.length"
            @click="
              expanded.length
                ? (expanded = [])
                : (expanded = [
                    item.tag.id,
                    ...item.relatedTags.map((x) => x.id),
                  ])
            "
          >
            <v-icon color="primary">
              {{ expanded.length ? "expand_less" : "expand_more" }}
            </v-icon>
          </v-btn>
          <v-menu bottom left>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                @click="
                  editRelatedTags = {
                    id: item.tag.id,
                    relatedTags: item.relatedTags,
                  }
                "
                >{{
                  `${$t("dialog.change")} ${$t(
                    "management.tag.column.relatedTags"
                  ).toLowerCase()}`
                }}
              </v-list-item>
              <v-list-item
                v-if="!(item.employees && item.employees.length)"
                @click="handleActivation(item.tag)"
              >
                {{
                  item.tag.isActive
                    ? $t("dialog.deactivate")
                    : $t("dialog.activate")
                }}
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-data-table>
    <related-tags-dialogue
      :editRelatedTags="editRelatedTags"
      @hide="editRelatedTags = null"
      @update:relatedTags="updateRelatedTags"
      :tagStats="tagStats"
    />
    <alert
      :alert="alert"
      :title="
        `${$t('dialog.deactivate')} ${(alert && alert.description) ||
          (alert && alert.name)}?`
      "
      :text="alertText"
      :okText="$t('dialog.deactivate')"
      @cancel="alert = null"
      @ok="setTagIsActive(alert, !alert.isActive), (alert = null)"
    />
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

import RelatedTagsDialogue from "@/components/management/RelatedTagsDialogue.vue";
import AddNewTag from "@/components/control/AddNewTag";
import EmployeesTableDialogWithQuery from "@/components/employee/EmployeesTableDialogWithQuery.vue";
import InstructionsTableDialogWithQuery from "@/components/instruction/InstructionsTableDialogWithQuery.vue";
import Alert from "@/components/Alert";

import TagsStatsQuery from "@/graphql/queries/TagStats.gql";
import TagCategoriesQuery from "@/graphql/TagCategories.gql";

import ChangeTagDescriptionQuery from "@/graphql/mutation/tag/ChangeTagDescription.gql";
import ChangeTagNameQuery from "@/graphql/mutation/tag/ChangeTagName.gql";
import ChangeTagExplanationQuery from "@/graphql/mutation/tag/ChangeTagExplanation.gql";
import SetTagIsActiveQuery from "@/graphql/mutation/tag/SetTagIsActive.gql";

import {
  createTagCategories,
  createEmployeesSearch,
  getMissingParentTags,
} from "@/utils/common.js";

export default {
  components: {
    RelatedTagsDialogue,
    AddNewTag,
    EmployeesTableDialogWithQuery,
    InstructionsTableDialogWithQuery,
    Alert,
  },
  data() {
    return {
      search: "",
      newDescription: null,
      expanded: [],
      editRelatedTags: null,
      showActive: true,
      newName: null,
      newExplanation: null,
      alert: null,
    };
  },

  computed: {
    ...mapGetters(["role"]),

    headers() {
      const h = [
        {
          text: "ID",
          value: "tag.id",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.tag.column.name"),
          value: "tag.name",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.tag.column.category"),
          value: "tag.category",
          class: "text-uppercase subheading text-truncate",
          sort: (a, b) => this.categorySort(a, b),
        },
        {
          text: this.$i18n.t("management.tag.column.description"),
          value: "tag.description",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.tag.column.explanation"),
          value: "tag.explanation",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.tag.column.employees"),
          value: "employeeIds",
          class: "text-uppercase subheading text-truncate",
          sort: (a, b) => a.length - b.length,
        },
        {
          text: this.$i18n.t("management.tag.column.instructions"),
          value: "instructionIds",
          class: "text-uppercase subheading text-truncate",
          sort: (a, b) => a.length - b.length,
        },
        {
          text: this.$i18n.t("management.tag.column.managers"),
          value: "managers",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("management.tag.column.relatedTags"),
          value: "relatedTags",
          class: "text-uppercase subheading text-truncate",
        },
      ];
      if (this.role("admin", "safetyRepresentative")) {
        h.push({
          value: "menu",
          sortable: false,
          align: "right",
        });
      }
      return h;
    },
    filteredTagStats() {
      return this.expanded.length
        ? this.tagStats.filter(
            (x) =>
              this.expanded.includes(x.tag.id) &&
              (this.showActive ? x.tag.isActive : !x.tag.isActive)
          )
        : (this.tagStats &&
            this.tagStats.filter((x) =>
              this.showActive ? x.tag.isActive : !x.tag.isActive
            )) ||
            this.tagStats;
    },
    alertText() {
      if (!this.alert) return "";

      const tag = this.tagStats.find((ts) => ts.tag.id === this.alert.id);

      return `
    ${this.$t("management.tag.alert.employees")} ${tag.employeeIds.length}
    ${this.$t("management.tag.alert.instructions")} ${tag.instructionIds.length}
  `;
    },
  },
  methods: {
    categorySort(a, b) {
      const dep = "department";
      if (a == dep && b != dep) {
        return 2;
      }
      if (a != dep && b == dep) {
        return -1;
      }
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    },
    remapEmployees(employees) {
      return (
        (employees &&
          employees.map((e) => {
            const missingParentTags = getMissingParentTags(e);
            const search = `${createEmployeesSearch(e)}${
              missingParentTags.length ? "|*" : ""
            }`;
            return {
              ...e,
              search,
              ...createTagCategories(this.tagCategories, e.tags),
              missingParentTags,
            };
          })) ||
        []
      );
    },
    updateRelatedTags(e) {
      const item = this.tagStats.find((x) => x.tag.id == e.id);
      if (!item) {
        return;
      }

      item.relatedTags = e.relatedTags;
    },
    changeTagDescription(tag, description) {
      this.$apollo
        .mutate({
          mutation: ChangeTagDescriptionQuery,
          variables: {
            id: tag.id,
            description,
          },
        })
        .then(() => {
          tag.description = description;
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `Failed to change description. Error: ${err}`,
          })
        )
        .finally(() => {
          this.newDescription = null;
        });
    },
    changeTagName(tag, name) {
      this.$apollo
        .mutate({
          mutation: ChangeTagNameQuery,
          variables: {
            id: tag.id,
            name,
          },
        })
        .then(() => {
          tag.name = name;
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `Failed to change name. Error: ${err}`,
          })
        )
        .finally(() => {
          this.newName = null;
        });
    },
    changeTagExplanation(tag, explanation) {
      if (!explanation) {
        explanation = null;
      }
      this.$apollo
        .mutate({
          mutation: ChangeTagExplanationQuery,
          variables: {
            id: tag.id,
            explanation: explanation,
          },
        })
        .then(() => {
          tag.explanation = explanation;
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `Failed to change explanation. Error: ${err}`,
          })
        )
        .finally(() => {
          this.newExplanation = null;
        });
    },
    setTagIsActive(tag, state = false) {
      this.$apollo
        .mutate({
          mutation: SetTagIsActiveQuery,
          variables: {
            id: tag.id,
            state,
          },
        })
        .then(() => {
          this.$apollo.queries.tagStats.refetch();
          tag.isActive = state;
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `Failed to change tag isActive to ${state}. Error: ${err}`,
          })
        );
    },
    searchFilter(value, search, item) {
      return (
        !search ||
        search
          .toLowerCase()
          .split(" ")
          .every((w) => item.search.includes(w))
      );
    },
    handleActivation(tag) {
      if (tag.isActive) {
        this.alert = tag;
      } else {
        this.setTagIsActive(tag, !tag.isActive);
      }
    },
  },
  apollo: {
    tagCategories: {
      query: TagCategoriesQuery,
    },
    tagStats: {
      query: TagsStatsQuery,
      update(data) {
        return (
          data.tagStats &&
          data.tagStats.length &&
          data.tagStats.map((e) => ({
            ...e,
            search: [
              e.tag.id,
              e.tag.name,
              e.tag.description,
              e.tag.category,
              ...e.managers.map((m) => m.firstName),
              ...e.managers.map((m) => m.lastName),
              ...e.relatedTags.map((t) => t.name),
              ...e.relatedTags.map((t) => t.description),
            ]
              .join("|")
              .toLowerCase(),
          }))
        );
      },
    },
  },
};
</script>
